import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import {
  SendPushNotification as sendPushNotificationApi,
  getAllMobileNos,
} from "../../api/Messages";

import { batchList } from "../../api/Batch";

import { courseList } from "../../api/Courses";

import Master from "../message/Master";

import Header from "../master/SideBar";

import Nav from "../master/NavBar";

import { LoadingDiv, SweetAlertShow } from "../master/Master";
import { busList, busStudentsList } from "../../api/Transport";
import { StaffList } from "../../api/Staff";

const STORAGE_KEY = "push_notification_draft_v2";

const MAX_VISIBLE = 250;

const SEND_DELAY_MS = 350;

const AUDIENCE_TYPES = [
  {
    value: "single",
    label: "Single Students",
    icon: "ri-user-line",
    desc: "Choose individual students",
  },

  {
    value: "batch",
    label: "Batch Students",
    icon: "ri-group-line",
    desc: "Select one or more batches",
  },

  {
    value: "course",
    label: "Course Students",
    icon: "ri-book-open-line",
    desc: "Select one or more courses",
  },
  {
    value: "staff",
    label: "Staff",
    icon: "ri-user-settings-line",
    desc: "Select staff members / teachers",
  },
];

const INITIAL = {
  audienceType: "batch",

  sessionId: "",

  batchIds: [],

  busId: "",

  courseIds: [],

  studentIds: [],

  title: "",

  message: "",

  file: "",
};

const unwrapList = (response) => {
  const candidates = [
    response?.data?.data?.data,

    response?.data?.data,

    response?.data?.students,

    response?.data?.batches,

    response?.data?.recipients,
  ];

  return candidates.find(Array.isArray) || [];
};

const getName = (s) => s?.name || s?.student_name || s?.full_name || "Student";

const getId = (s) => s?.student_id ?? s?.id ?? s?.user_id;

const getRecipientKey = (s, index = 0) =>
  String(
    getId(s) ??
      s?.mobile ??
      s?.phone ??
      s?.mobile_no ??
      s?.registration_no ??
      `recipient-${index}`,
  );

const getMobile = (s) =>
  s?.mobile ?? s?.phone ?? s?.mobile_no ?? s?.whatsapp_no ?? "";

const collectNestedIds = (
  obj,
  directKey,
  objectKey,
  output = new Set(),
  depth = 0,
) => {
  if (!obj || typeof obj !== "object" || depth > 5) return output;

  if (
    obj[directKey] !== undefined &&
    obj[directKey] !== null &&
    obj[directKey] !== ""
  ) {
    output.add(String(obj[directKey]));
  }

  if (
    obj[objectKey] &&
    typeof obj[objectKey] === "object" &&
    obj[objectKey]?.id !== undefined
  ) {
    output.add(String(obj[objectKey].id));
  }

  Object.values(obj).forEach((value) => {
    if (value && typeof value === "object") {
      if (Array.isArray(value)) {
        value.forEach((item) =>
          collectNestedIds(item, directKey, objectKey, output, depth + 1),
        );
      } else {
        collectNestedIds(value, directKey, objectKey, output, depth + 1);
      }
    }
  });

  return output;
};

const getBatchIds = (s) => [...collectNestedIds(s, "batch_id", "batch")];
const getBatchId = (s) => getBatchIds(s)[0] ?? s?.batch_id ?? s?.batch?.id;

const getBatchName = (s) =>
  s?.batch_name ??
  (typeof s?.batch === "string" ? s.batch : null) ??
  s?.batch?.title ??
  s?.batch?.name ??
  s?.student_session?.batch_name ??
  s?.student_session?.batch?.name ??
  s?.admission?.batch_name ??
  s?.admission?.batch?.name ??
  "";

const getCourseId = (s) => s?.course_id ?? s?.course?.id;

const getCourseName = (s) =>
  s?.course_name ?? s?.course?.name ?? s?.course ?? "";

const getSessionId = (s) =>
  s?.session_id ??
  s?.session?.id ??
  s?.student_session?.session_id ??
  s?.admission?.session_id;

const getSessionName = (s) => s?.session_name ?? s?.session?.name ?? "";

const numberValue = (value) => {
  const n = Number.parseFloat(value ?? 0);

  return Number.isFinite(n) ? n : 0;
};

const getTotalDue = (s) =>
  numberValue(
    s?.all_session_due ??
      s?.grand_due ??
      s?.batch_total_due ??
      s?.total_due ??
      0,
  );

const getTotalPaid = (s) =>
  numberValue(
    s?.all_session_paid ??
      s?.grand_paid ??
      s?.batch_total_paid ??
      s?.total_paid ??
      0,
  );

const isDue = (s) => getTotalDue(s) > 0;

// Paid means there is no remaining due amount.
const isPaid = (s) => getTotalDue(s) <= 0;

const isBusStudent = (student) => {
  return (
    student?.is_bus === true ||
    student?.is_bus === 1 ||
    student?.is_bus === "1" ||
    String(student?.bus_student || "").toLowerCase() === "yes"
  );
};

const isHostelStudent = (student) => {
  return (
    student?.is_hostel === true ||
    student?.is_hostel === 1 ||
    student?.is_hostel === "1" ||
    String(student?.hostel_student || "").toLowerCase() === "yes"
  );
};

const matchesStudentType = (student, filter) => {
  const bus = isBusStudent(student);
  const hostel = isHostelStudent(student);

  switch (filter) {
    case "bus":
      return bus;
    case "hostel":
      return hostel;
    case "bus_hostel":
      return bus && hostel;
    case "day_scholar":
      return !bus && !hostel;
    default:
      return true;
  }
};

const flattenObject = (obj, prefix = "", output = {}) => {
  if (!obj || typeof obj !== "object") return output;

  Object.entries(obj).forEach(([key, value]) => {
    const next = prefix ? `${prefix}.${key}` : key;

    if (value && typeof value === "object" && !Array.isArray(value)) {
      flattenObject(value, next, output);
    } else {
      output[next] = value;
    }
  });

  return output;
};

const getDynamicValue = (student, key) => {
  if (!student || !key) return "";

  const flat = flattenObject(student);

  if (flat[key] !== undefined && flat[key] !== null) return String(flat[key]);

  const aliases = {
    student_name: ["student_name", "name"],

    name: ["name", "student_name"],

    father_name: ["father_name", "father"],

    mobile: ["mobile", "phone", "mobile_no"],

    phone: ["phone", "mobile"],

    batch: ["batch", "batch_name"],

    batch_name: ["batch_name", "batch"],

    session: ["session", "session_name"],

    session_name: ["session_name", "session"],

    total_fee: ["total_fee", "batch_total_fee"],

    final_fee: ["batch_total_fee", "final_fee", "total_fee"],

    paid_fee: ["batch_total_paid", "total_paid", "paid_fee"],

    total_paid: ["batch_total_paid", "total_paid", "paid_fee"],

    due_fee: ["batch_total_due", "total_due", "due_fee"],

    total_due: ["batch_total_due", "total_due", "due_fee"],

    fee_status: ["fee_status"],
  };

  const keys = aliases[key] || [key];

  for (const k of keys) {
    if (flat[k] !== undefined && flat[k] !== null) return String(flat[k]);
  }

  return "";
};

const formatFieldLabel = (key) =>
  String(key || "")
    .split(".")

    .pop()

    .replaceAll("_", " ")

    .replace(/\b\w/g, (c) => c.toUpperCase());

const resolveMessage = (text, student) => {
  if (!text) return "";

  return text.replace(/{{\s*([^{}]+?)\s*}}/g, (_, key) =>
    getDynamicValue(student, key.trim()),
  );
};

const normalizeStudents = (list) =>
  (Array.isArray(list) ? list : [])
    .filter(Boolean)
    .map((item, index) => ({
      ...item,
      __id: getRecipientKey(item, index),
    }))
    .filter((item) => item.__id);

function MultiSelect({
  label,
  items,
  values,
  onChange,
  placeholder = "Select...",
  disabled = false,
}) {
  const [open, setOpen] = useState(false);

  const [search, setSearch] = useState("");

  const ref = useRef(null);

  const selected = new Set(values.map(String));

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();

    return q
      ? items.filter((x) => String(x.label).toLowerCase().includes(q))
      : items;
  }, [items, search]);

  useEffect(() => {
    const fn = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };

    document.addEventListener("mousedown", fn);

    return () => document.removeEventListener("mousedown", fn);
  }, []);

  const toggle = (id) => {
    const key = String(id);

    onChange(
      selected.has(key)
        ? values.filter((x) => String(x) !== key)
        : [...values, key],
    );
  };

  return (
    <div ref={ref} className="position-relative">
      <label className="form-label fw-semibold">{label}</label>

      <button
        type="button"
        className="form-select text-start"
        disabled={disabled}
        onClick={() => setOpen((v) => !v)}
      >
        {values.length ? `${values.length} selected` : placeholder}
      </button>

      {open && !disabled && (
        <div
          className="position-absolute w-100 bg-body border rounded-3 shadow-lg mt-1"
          style={{ zIndex: 1080 }}
        >
          <div className="p-2 border-bottom">
            <input
              className="form-control"
              autoFocus
              placeholder="Search..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>

          <div style={{ maxHeight: 280, overflowY: "auto" }}>
            {filtered.length ? (
              filtered.map((item) => {
                const checked = selected.has(String(item.id));

                return (
                  <button
                    key={item.id}
                    type="button"
                    className="w-100 border-0 bg-transparent px-3 py-2 text-start d-flex align-items-center gap-2"
                    onClick={() => toggle(item.id)}
                  >
                    <span
                      className={`badge rounded-pill ${checked ? "bg-primary" : "bg-label-secondary"}`}
                    >
                      {checked ? "✓" : ""}
                    </span>

                    <span className="flex-grow-1">{item.label}</span>
                  </button>
                );
              })
            ) : (
              <div className="p-4 text-center text-muted">No options found</div>
            )}
          </div>

          <div className="border-top p-2 d-flex gap-2">
            <button
              type="button"
              className="btn btn-sm btn-outline-primary"
              onClick={() => onChange(filtered.map((x) => String(x.id)))}
            >
              Select visible
            </button>

            <button
              type="button"
              className="btn btn-sm btn-outline-secondary"
              onClick={() => onChange([])}
            >
              Clear
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function StudentPicker({
  students,
  values,
  onChange,
  feeFilter,
  rteFilter,
  studentTypeFilter,
  setFeeFilter,
  setRteFilter,
  setStudentTypeFilter,
}) {
  const [search, setSearch] = useState("");
  const selected = new Set(values.map(String));

  const counts = useMemo(
    () => ({
      all: students.length,
      bus: students.filter(isBusStudent).length,
      hostel: students.filter(isHostelStudent).length,
      busHostel: students.filter((s) => isBusStudent(s) && isHostelStudent(s))
        .length,
      dayScholar: students.filter(
        (s) => !isBusStudent(s) && !isHostelStudent(s),
      ).length,
    }),
    [students],
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();

    return students
      .map((student, index) => ({ student, index }))
      .filter(({ student }) => {
        if (!matchesStudentType(student, studentTypeFilter)) return false;
        if (feeFilter === "due" && !isDue(student)) return false;
        if (feeFilter === "paid" && !isPaid(student)) return false;

        const rteStatus = String(student?.rte_status || "")
          .trim()
          .toUpperCase();
        if (rteFilter === "rte" && rteStatus !== "RTE") return false;
        if (rteFilter === "non_rte" && rteStatus !== "NON-RTE") return false;

        if (!q) return true;
        return [
          getName(student),
          getMobile(student),
          student?.registration_no,
          getBatchName(student),
          student?.father_name,
          student?.mother_name,
          student?.rte_status,
          student?.student_type,
          student?.bus_student,
          student?.hostel_student,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase()
          .includes(q);
      })
      .sort((a, b) => {
        const ad = isDue(a.student),
          bd = isDue(b.student);
        if (ad !== bd) return ad ? -1 : 1;
        return getName(a.student).localeCompare(getName(b.student));
      });
  }, [students, search, feeFilter, rteFilter, studentTypeFilter]);

  const toggle = (id) => {
    const key = String(id);
    onChange(
      selected.has(key)
        ? values.filter((x) => String(x) !== key)
        : [...values, key],
    );
  };

  const visibleIds = filtered.map(({ student }) =>
    String(student.__id || getRecipientKey(student)),
  );
  const selectOnlyVisible = () => onChange(visibleIds);
  const addVisible = () =>
    onChange([...new Set([...values.map(String), ...visibleIds])]);
  const unselectVisible = () => {
    const visibleSet = new Set(visibleIds);
    onChange(values.filter((id) => !visibleSet.has(String(id))));
  };

  const typeFilters = [
    {
      value: "all",
      label: "All Students",
      icon: "ri-group-line",
      count: counts.all,
    },
    {
      value: "bus",
      label: "Bus Students",
      icon: "ri-bus-line",
      count: counts.bus,
    },
    {
      value: "hostel",
      label: "Hostel Students",
      icon: "ri-hotel-bed-line",
      count: counts.hostel,
    },
    {
      value: "bus_hostel",
      label: "Bus + Hostel",
      icon: "ri-building-2-line",
      count: counts.busHostel,
    },
    {
      value: "day_scholar",
      label: "Day Scholars",
      icon: "ri-user-line",
      count: counts.dayScholar,
    },
  ];

  const chooseType = (filter) => {
    setStudentTypeFilter(filter);
    // Selecting an audience type immediately makes the send list exactly that type.
    const matchingIds = students
      .filter((student) => matchesStudentType(student, filter))
      .map((student) => String(student.__id || getRecipientKey(student)));
    onChange(matchingIds);
  };

  return (
    <div>
      <div className="border rounded-4 p-3 mb-3 bg-light">
        <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
          <div>
            <div className="fw-bold">
              <i className="ri-filter-3-line me-2 text-primary" />
              Student Type
            </div>
            <small className="text-muted">
              Select Bus, Hostel, Bus + Hostel or Day Scholar students.
            </small>
          </div>
          <span className="badge bg-primary px-3 py-2">
            {filtered.length} matching
          </span>
        </div>
        <div className="row g-2">
          {typeFilters.map((item) => {
            const active = studentTypeFilter === item.value;
            return (
              <div key={item.value} className="col-6 col-md-4 col-xl">
                <button
                  type="button"
                  className={`btn w-100 h-100 text-start p-3 ${active ? "btn-primary" : "btn-outline-primary"}`}
                  onClick={() => chooseType(item.value)}
                >
                  <div className="d-flex align-items-center justify-content-between">
                    <i className={`${item.icon} fs-4`} />
                    <span
                      className={`badge ${active ? "bg-white text-primary" : "bg-label-primary"}`}
                    >
                      {item.count}
                    </span>
                  </div>
                  <div className="fw-bold mt-2">{item.label}</div>
                </button>
              </div>
            );
          })}
        </div>
      </div>

      <div className="d-flex flex-wrap gap-2 align-items-center mb-3">
        <div className="input-group flex-grow-1" style={{ minWidth: 260 }}>
          <span className="input-group-text">
            <i className="ri-search-line" />
          </span>
          <input
            className="form-control"
            placeholder="Search student, registration, mobile..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        {["all", "due", "paid"].map((x) => (
          <button
            key={x}
            type="button"
            className={`btn ${feeFilter === x ? "btn-primary" : "btn-outline-primary"}`}
            onClick={() => {
              setFeeFilter(x);
              const ids = students
                .filter((student) =>
                  matchesStudentType(student, studentTypeFilter),
                )
                .filter(
                  (student) =>
                    x === "all" ||
                    (x === "due" ? isDue(student) : isPaid(student)),
                )
                .filter((student) => {
                  const rte = String(student?.rte_status || "")
                    .trim()
                    .toUpperCase();
                  return (
                    rteFilter === "all" ||
                    (rteFilter === "rte" ? rte === "RTE" : rte === "NON-RTE")
                  );
                })
                .map((student) =>
                  String(student.__id || getRecipientKey(student)),
                );
              onChange(ids);
            }}
          >
            {x === "all" ? "All Fees" : x === "due" ? "Due Fees" : "Paid"}
          </button>
        ))}
      </div>

      <div className="d-flex flex-wrap gap-2 align-items-center mb-3">
        <span className="small fw-semibold text-muted me-1">RTE:</span>
        {["all", "rte", "non_rte"].map((x) => (
          <button
            key={x}
            type="button"
            className={`btn btn-sm ${rteFilter === x ? "btn-primary" : "btn-outline-primary"}`}
            onClick={() => {
              setRteFilter(x);
              const ids = students
                .filter((student) =>
                  matchesStudentType(student, studentTypeFilter),
                )
                .filter(
                  (student) =>
                    feeFilter === "all" ||
                    (feeFilter === "due" ? isDue(student) : isPaid(student)),
                )
                .filter((student) => {
                  const rte = String(student?.rte_status || "")
                    .trim()
                    .toUpperCase();
                  return (
                    x === "all" ||
                    (x === "rte" ? rte === "RTE" : rte === "NON-RTE")
                  );
                })
                .map((student) =>
                  String(student.__id || getRecipientKey(student)),
                );
              onChange(ids);
            }}
          >
            {x === "all" ? "All Students" : x === "rte" ? "RTE" : "Non-RTE"}
          </button>
        ))}
      </div>

      <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-2">
        <div>
          <strong>{values.length}</strong> selected{" "}
          <span className="small text-muted">/ {students.length} students</span>{" "}
          <span className="small text-primary">
            ({filtered.length} visible)
          </span>
        </div>
        <div className="d-flex flex-wrap gap-2">
          <button
            type="button"
            className="btn btn-sm btn-primary"
            disabled={!visibleIds.length}
            onClick={selectOnlyVisible}
          >
            <i className="ri-checkbox-multiple-line me-1" />
            Select Only Visible
          </button>
          <button
            type="button"
            className="btn btn-sm btn-outline-primary"
            disabled={!visibleIds.length}
            onClick={addVisible}
          >
            Add Visible
          </button>
          <button
            type="button"
            className="btn btn-sm btn-outline-warning"
            disabled={!visibleIds.length}
            onClick={unselectVisible}
          >
            Unselect Visible
          </button>
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary"
            onClick={() => onChange([])}
          >
            Clear All
          </button>
        </div>
      </div>

      <div
        className="border rounded-3"
        style={{
          height: 420,
          maxHeight: 420,
          overflowY: "auto",
          overflowX: "hidden",
        }}
      >
        {filtered.slice(0, MAX_VISIBLE).map(({ student }) => {
          const id = String(student.__id || getRecipientKey(student));
          const checked = selected.has(id);
          const due = getTotalDue(student);
          const bus = isBusStudent(student);
          const hostel = isHostelStudent(student);

          return (
            <label
              key={id}
              className={`d-flex align-items-center gap-3 px-3 py-3 border-bottom cursor-pointer ${checked ? "bg-label-primary" : ""}`}
            >
              <input
                type="checkbox"
                className="form-check-input"
                checked={checked}
                onChange={() => toggle(id)}
              />
              <div className="avatar avatar-sm">
                <span className="avatar-initial rounded-circle bg-label-primary">
                  {getName(student).charAt(0).toUpperCase()}
                </span>
              </div>
              <div className="flex-grow-1 min-w-0">
                <div className="d-flex flex-wrap align-items-center gap-2">
                  <span className="fw-semibold text-truncate">
                    {getName(student)}
                  </span>
                  {bus && (
                    <span className="badge bg-label-primary">
                      <i className="ri-bus-line me-1" />
                      Bus
                    </span>
                  )}
                  {hostel && (
                    <span className="badge bg-label-warning">
                      <i className="ri-hotel-bed-line me-1" />
                      Hostel
                    </span>
                  )}
                  {!bus && !hostel && (
                    <span className="badge bg-label-secondary">
                      Day Scholar
                    </span>
                  )}
                  {student?.rte_status && (
                    <span
                      className={`badge ${String(student.rte_status).toUpperCase() === "RTE" ? "bg-label-success" : "bg-label-secondary"}`}
                    >
                      {student.rte_status}
                    </span>
                  )}
                </div>
                <div className="small text-muted mt-1">
                  {student?.registration_no || "—"} •{" "}
                  {getBatchName(student) || "No batch"} •{" "}
                  {getMobile(student) || "No mobile"}
                </div>
                <div className="small mt-2 d-flex flex-wrap gap-2">
                  <span className="badge bg-label-danger">
                    Batch Due ₹
                    {numberValue(student?.batch_total_due).toFixed(2)}
                  </span>
                  <span className="badge bg-label-success">
                    Batch Paid ₹
                    {numberValue(student?.batch_total_paid).toFixed(2)}
                  </span>
                  {bus && (
                    <span className="badge bg-label-primary">
                      <i className="ri-bus-line me-1" />
                      Bus Due ₹{numberValue(student?.bus_due).toFixed(2)}
                    </span>
                  )}
                  {hostel && (
                    <span className="badge bg-label-warning">
                      <i className="ri-hotel-bed-line me-1" />
                      Hostel Due ₹{numberValue(student?.hostel_due).toFixed(2)}
                    </span>
                  )}
                  <span className="badge bg-label-dark">
                    Total Due ₹{getTotalDue(student).toFixed(2)}
                  </span>
                </div>
              </div>
              <span
                className={`badge ${due > 0 ? "bg-label-danger" : "bg-label-success"}`}
              >
                {due > 0 ? `Due ₹${due.toFixed(2)}` : "Paid"}
              </span>
            </label>
          );
        })}
        {!filtered.length && (
          <div className="p-5 text-center text-muted">
            <i className="ri-user-search-line fs-1 d-block mb-2" />
            <div className="fw-semibold">No students found</div>
            <small>Change student type, fee, RTE or search filter.</small>
          </div>
        )}
      </div>
    </div>
  );
}

export default function SendPushNotification() {
  const [form, setForm] = useState(INITIAL);

  const [sessions, setSessions] = useState([]);

  const [batches, setBatches] = useState([]);

  const [buses, setBuses] = useState([]);

  const [courses, setCourses] = useState([]);

  const [students, setStudents] = useState([]);
  const [staffMembers, setStaffMembers] = useState([]);
  const [loadingStaff, setLoadingStaff] = useState(false);

  const [loading, setLoading] = useState(false);

  const [loadingStudents, setLoadingStudents] = useState(false);

  const [feeFilter, setFeeFilter] = useState("all");
  const [rteFilter, setRteFilter] = useState("all");
  const [studentTypeFilter, setStudentTypeFilter] = useState("all");

  const [sending, setSending] = useState(false);

  const [progress, setProgress] = useState({
    total: 0,
    sent: 0,
    failed: 0,
    pending: 0,
    current: null,
  });

  const [failures, setFailures] = useState([]);

  const [previewOpen, setPreviewOpen] = useState(false);

  const [messagePreviewOpen, setMessagePreviewOpen] = useState(false);

  const [leaveGuardOpen, setLeaveGuardOpen] = useState(false);

  const pendingNavigationRef = useRef(null);

  const stopRef = useRef(false);

  const busStudentsRequestRef = useRef(0);
  const batchStudentsRequestRef = useRef(0);

  const mountedRef = useRef(true);

  const setField = (key, value) => setForm((p) => ({ ...p, [key]: value }));

  // Immediately clear the previous audience and invalidate in-flight requests
  // whenever the user switches between Batch and Bus.
  const handleAudienceChange = (audienceType) => {
    if (audienceType === form.audienceType) return;

    batchStudentsRequestRef.current += 1;
    busStudentsRequestRef.current += 1;

    setLoadingStudents(false);
    setStudents([]);
    setStaffMembers([]);
    setFeeFilter("all");
    setRteFilter("all");
    setStudentTypeFilter("all");

    setForm((prev) => ({
      ...prev,
      audienceType,
      batchIds: [],
      busId: "",
      courseIds: [],
      studentIds: [],
    }));
  };

  useEffect(() => {
    mountedRef.current = true;

    try {
      const saved = JSON.parse(localStorage.getItem(STORAGE_KEY) || "null");

      if (saved?.form) setForm({ ...INITIAL, ...saved.form });
    } catch {}

    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ form }));
  }, [form]);

  useEffect(() => {
    let storedSessions = [];

    try {
      storedSessions = JSON.parse(localStorage.getItem("sessions") || "[]");
    } catch {
      storedSessions = [];
    }

    setSessions(storedSessions);

    const active = storedSessions.find(
      (session) => Number(session?.status) === 1,
    );

    if (active?.id && !form.sessionId) setField("sessionId", String(active.id));
  }, []);

  useEffect(() => {
    if (!form.sessionId) {
      setBatches([]);
      setField("batchIds", []);
      return;
    }

    let cancelled = false;

    const load = async () => {
      setLoading(true);

      try {
        const response = await batchList(
          `&session_id=${encodeURIComponent(form.sessionId)}`,
        );

        const list = response?.data?.data || [];

        if (!cancelled) setBatches(Array.isArray(list) ? list : []);
      } catch (e) {
        if (!cancelled) setBatches([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [form.sessionId]);

  useEffect(() => {
    if (form.audienceType !== "bus" || !form.sessionId) {
      setBuses([]);
      return;
    }

    let cancelled = false;

    const load = async () => {
      setLoading(true);

      try {
        const response = await busList(
          `&session_id=${encodeURIComponent(form.sessionId)}`,
        );

        if (cancelled) return;

        if (!response?.data?.status) {
          setBuses([]);
          return;
        }

        setBuses(Array.isArray(response?.data?.data) ? response.data.data : []);
      } catch (e) {
        if (!cancelled) {
          setBuses([]);
          SweetAlertShow(
            "error",
            "Buses",
            e?.response?.data?.message || e?.message || "Unable to load buses",
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [form.audienceType, form.sessionId]);

  useEffect(() => {
    if (!form.sessionId) {
      setCourses([]);
      setField("courseIds", []);
      return;
    }

    let cancelled = false;

    const load = async () => {
      setLoading(true);

      try {
        const response = await courseList(
          `&session_id=${encodeURIComponent(form.sessionId)}`,
        );

        const list = response?.data?.data || [];

        if (!cancelled) setCourses(Array.isArray(list) ? list : []);
      } catch (e) {
        if (!cancelled) setCourses([]);

        SweetAlertShow(
          "error",
          "Courses",
          e?.response?.data?.message || e?.message || "Unable to load courses",
        );
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [form.sessionId]);

  /*
   * IMPORTANT:
   * Do not load the complete student master here.
   * Recipients are fetched only after session + batch selection,
   * using the same /get-all-mobile-nos flow as the WhatsApp sender.
   */
  const loadBatchStudents = useCallback(async () => {
    const requestId = ++batchStudentsRequestRef.current;

    if (form.audienceType !== "batch") {
      setLoadingStudents(false);
      return;
    }

    if (!form.sessionId || !form.batchIds.length) {
      setStudents([]);
      setForm((prev) => ({ ...prev, studentIds: [] }));
      setLoadingStudents(false);
      return;
    }

    // Do not keep bus/previous-batch recipients visible while the new
    // batch selection is being fetched.
    setStudents([]);
    setForm((prev) => ({ ...prev, studentIds: [] }));
    setLoadingStudents(true);

    try {
      let query =
        `&type=${encodeURIComponent("Batch Students")}` +
        `&session_id=${encodeURIComponent(form.sessionId)}`;

      form.batchIds.forEach((batchId) => {
        query += `&batch_ids[]=${encodeURIComponent(batchId)}`;
      });

      const response = await getAllMobileNos(query);

      if (
        requestId !== batchStudentsRequestRef.current ||
        form.audienceType !== "batch"
      )
        return;

      if (!response?.data?.status) {
        setStudents([]);
        setForm((prev) => ({ ...prev, studentIds: [] }));
        SweetAlertShow(
          "info",
          "No Students",
          response?.data?.message ||
            "No students found for the selected batch.",
        );
        return;
      }

      const recipients = normalizeStudents(
        response?.data?.data?.recipients || [],
      );
      setStudents(recipients);

      // By default select every fetched student.
      // User can then open Select / Edit Students and remove anyone.
      setForm((prev) => ({
        ...prev,
        studentIds: recipients.map((student) => student.__id),
      }));
    } catch (e) {
      if (
        requestId === batchStudentsRequestRef.current &&
        form.audienceType === "batch"
      ) {
        setStudents([]);
        setForm((prev) => ({ ...prev, studentIds: [] }));
        SweetAlertShow(
          "error",
          "Students",
          e?.response?.data?.message ||
            e?.message ||
            "Unable to load batch students",
        );
      }
    } finally {
      if (requestId === batchStudentsRequestRef.current) {
        setLoadingStudents(false);
      }
    }
  }, [form.audienceType, form.sessionId, form.batchIds]);

  useEffect(() => {
    loadBatchStudents();
  }, [loadBatchStudents]);

  useEffect(() => {
    if (form.audienceType !== "staff") {
      setLoadingStaff(false);
      return;
    }

    let cancelled = false;

    const loadStaff = async () => {
      setLoadingStaff(true);
      try {
        const response = await StaffList();
        const candidates = [
          response?.data?.data,
          response?.data?.data?.data,
          response?.data?.staff,
        ];
        const list = candidates.find(Array.isArray) || [];
        const normalized = list
          .filter((item) => item && (item.id ?? item.staff_id ?? item.user_id))
          .map((item, index) => {
            const id = item.id ?? item.staff_id ?? item.user_id;
            return {
              ...item,
              id,
              staff_id: item.staff_id ?? id,
              name: item.name ?? item.staff_name ?? item.full_name ?? "Staff",
              mobile: item.phone ?? item.mobile ?? item.mobile_no ?? "",
              phone: item.phone ?? item.mobile ?? item.mobile_no ?? "",
              __id: String(id ?? `staff-${index}`),
            };
          });

        if (!cancelled) {
          setStaffMembers(normalized);
          setForm((prev) => ({
            ...prev,
            studentIds: normalized.map((item) => item.__id),
          }));
        }
      } catch (e) {
        if (!cancelled) {
          setStaffMembers([]);
          setForm((prev) => ({ ...prev, studentIds: [] }));
          SweetAlertShow(
            "error",
            "Staff",
            e?.response?.data?.message ||
              e?.message ||
              "Unable to load staff list",
          );
        }
      } finally {
        if (!cancelled) setLoadingStaff(false);
      }
    };

    loadStaff();
    return () => {
      cancelled = true;
    };
  }, [form.audienceType]);

  const loadBusStudents = useCallback(async () => {
    const requestId = ++busStudentsRequestRef.current;

    if (form.audienceType !== "bus" || !form.sessionId || !form.busId) {
      setLoadingStudents(false);
      if (form.audienceType === "bus") {
        setStudents([]);
        setForm((prev) => ({ ...prev, studentIds: [] }));
      }
      return;
    }

    // Clear the previous list immediately when loading a different bus.
    setStudents([]);
    setForm((prev) => ({ ...prev, studentIds: [] }));
    setLoadingStudents(true);

    try {
      const query = [
        `bus_id=${encodeURIComponent(form.busId)}`,
        `session_id=${encodeURIComponent(form.sessionId)}`,
        "limit=all",
      ].join("&");

      const response = await busStudentsList(query);

      if (requestId !== busStudentsRequestRef.current) return;

      if (!response?.data?.status) {
        setStudents([]);
        setForm((prev) => ({ ...prev, studentIds: [] }));
        SweetAlertShow(
          "info",
          "No Bus Students",
          response?.data?.message || "No students found for the selected bus.",
        );
        return;
      }

      const rows = Array.isArray(response?.data?.data)
        ? response.data.data
        : [];
      const recipients = normalizeStudents(
        rows
          .filter((row) => row?.student)
          .map((row, index) => {
            const student = row.student;

            return {
              ...row,
              ...student,
              id: student.id ?? student.student_id,
              student_id: student.id ?? student.student_id,
              bus_student_id: row.id,
              name: student.name ?? student.student_name ?? "",
              registration_no: student.registration_no ?? "",
              father_name: student.father_name ?? "",
              mother_name: student.mother_name ?? "",
              mobile: student.phone ?? student.mobile ?? "",
              phone: student.phone ?? student.mobile ?? "",
              is_bus: true,
              bus_student: "Yes",
              student,
              bus: row.bus ?? null,
              stop: row.stop ?? null,
              __busRowIndex: index,
            };
          }),
      );

      setStudents(recipients);
      setForm((prev) => ({
        ...prev,
        studentIds: recipients.map((student) => student.__id),
      }));

      if (!recipients.length) {
        SweetAlertShow(
          "info",
          "No Bus Students",
          "No students were found for the selected bus and session.",
        );
      }
    } catch (e) {
      if (requestId === busStudentsRequestRef.current) {
        setStudents([]);
        setForm((prev) => ({ ...prev, studentIds: [] }));
        SweetAlertShow(
          "error",
          "Bus Students",
          e?.response?.data?.message ||
            e?.message ||
            "Unable to load bus students",
        );
      }
    } finally {
      if (requestId === busStudentsRequestRef.current)
        setLoadingStudents(false);
    }
  }, [form.audienceType, form.sessionId, form.busId]);

  useEffect(() => {
    loadBusStudents();
  }, [loadBusStudents]);

  const batchOptions = useMemo(
    () =>
      batches.map((b) => ({
        id: b?.id,
        label:
          b?.title ||
          b?.name ||
          b?.batch_name ||
          b?.course?.title ||
          `Batch #${b?.id}`,
      })),
    [batches],
  );

  const courseOptions = useMemo(
    () =>
      courses.map((c) => ({
        id: c?.id,
        label: c?.name || c?.course_name || `Course #${c?.id}`,
      })),
    [courses],
  );

  const selectedSession = useMemo(
    () => sessions.find((s) => String(s?.id) === String(form.sessionId)),
    [sessions, form.sessionId],
  );

  const sessionName = useMemo(
    () =>
      selectedSession?.title ||
      selectedSession?.name ||
      selectedSession?.session_name ||
      (form.sessionId ? `Session #${form.sessionId}` : ""),
    [selectedSession, form.sessionId],
  );

  const getBatchDisplayName = useCallback(
    (batch) =>
      batch?.title ||
      batch?.name ||
      batch?.batch_name ||
      batch?.course?.title ||
      `Batch #${batch?.id}`,
    [],
  );

  const getBusNumber = useCallback(
    (bus) =>
      bus?.bus_number ||
      bus?.vehicle_number ||
      bus?.registration_number ||
      bus?.vehicle_no ||
      bus?.bus_no ||
      bus?.number ||
      `Bus #${bus?.id}`,
    [],
  );

  const getBusDriver = useCallback(
    (bus) =>
      bus?.driver_name ||
      bus?.driver?.name ||
      bus?.driver?.full_name ||
      bus?.staff?.name ||
      bus?.driver?.staff_name ||
      bus?.name_of_driver ||
      "Driver not assigned",
    [],
  );

  const selectedBus = useMemo(
    () => buses.find((bus) => String(bus?.id) === String(form.busId)),
    [buses, form.busId],
  );

  const selectedBatchObjects = useMemo(() => {
    const ids = new Set(form.batchIds.map(String));
    return batches.filter((batch) => ids.has(String(batch?.id)));
  }, [batches, form.batchIds]);

  const selectedBatchNames = useMemo(
    () => selectedBatchObjects.map(getBatchDisplayName),
    [selectedBatchObjects, getBatchDisplayName],
  );

  const getStudentBatchDisplayName = useCallback(
    (student) => {
      const direct = getBatchName(student);
      if (direct) return direct;
      const ids = getBatchIds(student).map(String);
      const matched = batches.find((batch) => ids.includes(String(batch?.id)));
      return matched ? getBatchDisplayName(matched) : "—";
    },
    [batches, getBatchDisplayName],
  );

  const filteredAudienceStudents = useMemo(
    () => (form.audienceType === "staff" ? staffMembers : students),
    [form.audienceType, staffMembers, students],
  );

  const selectedStudents = useMemo(() => {
    const selected = new Set(form.studentIds.map(String));

    return filteredAudienceStudents.filter((s) =>
      selected.has(String(s.__id || getRecipientKey(s))),
    );
  }, [filteredAudienceStudents, form.studentIds]);

  // const dynamicFields = useMemo(() => {

  //     const source = selectedStudents.length ? selectedStudents : filteredAudienceStudents;

  //     const map = {};

  //     source.forEach((s) => Object.entries(flattenObject(s)).forEach(([key, value]) => {

  //         if (value !== null && value !== undefined && value !== "") map[key] = value;

  //     }));

  //     const common = ["name", "student_name", "father_name", "registration_no", "mobile", "batch", "batch_name", "session", "session_name", "total_fee", "total_paid", "total_due", "fee_status", "course", "course_name", "email"];

  //     return Object.keys(map).sort((a, b) => {

  //         const ai = common.indexOf(a), bi = common.indexOf(b);

  //         if (ai !== -1 && bi !== -1) return ai - bi;

  //         if (ai !== -1) return -1;

  //         if (bi !== -1) return 1;

  //         return a.localeCompare(b);

  //     });

  // }, [selectedStudents, filteredAudienceStudents]);

  const dynamicFields = useMemo(() => {
    return [
      "id",
      "name",
      "father_name",
      "mother_name",
      "registration_no",
      "mobile",

      "batch",
      "course",
      "fee_status",

      // Student Type / Facility
      "is_bus",
      "is_hostel",
      "bus_student",
      "hostel_student",
      "student_type",

      // Student Fee
      "batch_total_fee",
      "batch_total_paid",
      "batch_total_due",

      // Bus Fee
      "bus_final_fee",
      "bus_paid",
      "bus_due",

      // Hostel Fee
      "hostel_final_fee",
      "hostel_paid",
      "hostel_due",

      // Grand Fee
      "grand_final_fee",
      "grand_paid",
      "grand_due",

      // All Session Fee
      "all_session_total",
      "all_session_paid",
      "all_session_due",
    ];
  }, []);

  const insertVariable = (field) => {
    const token = `{{${field}}}`;

    setField(
      "message",
      `${form.message}${form.message && !form.message.endsWith(" ") ? " " : ""}${token}`,
    );
  };

  const previewStudent =
    selectedStudents[0] || filteredAudienceStudents[0] || {};

  const previewTitle = resolveMessage(form.title, previewStudent);

  const previewMessage = resolveMessage(form.message, previewStudent);

  const validate = () => {
    if (!form.audienceType) return "Please select an audience first.";

    if (form.audienceType === "batch" && !form.batchIds.length)
      return "Please select at least one batch.";

    if (form.audienceType === "bus" && !form.busId)
      return "Please select a bus.";

    if (form.audienceType === "staff" && !form.studentIds.length)
      return "Please select at least one staff member.";

    if (!form.title.trim()) return "Notification title is required.";

    if (!form.message.trim()) return "Message is required.";

    return "";
  };

  const sendOne = async (student) => {
    const id = getId(student);

    const mobile = getMobile(student);

    if (!id) {
      throw new Error(
        "Student ID is missing in get-all-mobile-nos recipient. Return student_id (or id/user_id) from that API.",
      );
    }

    const payload = {
      type: form.audienceType === "staff" ? "teacher" : "student",

      user_id: String(id),

      title: resolveMessage(form.title, student),

      description: resolveMessage(form.message, student),
    };

    if (form.file.trim()) payload.file = form.file.trim();

    const response = await sendPushNotificationApi(payload);

    if (!response?.data?.status) {
      const message = response?.data?.message;

      throw new Error(
        Array.isArray(message)
          ? message.join(", ")
          : message || "Push notification failed",
      );
    }

    return { id, mobile };
  };

  const handleSend = async () => {
    const error = validate();

    if (error) return SweetAlertShow("info", "Check Message", error);

    if (!selectedStudents.length)
      return SweetAlertShow(
        "info",
        "No Students",
        "No students are selected for sending.",
      );

    stopRef.current = false;

    setSending(true);

    setFailures([]);

    setProgress({
      total: selectedStudents.length,
      sent: 0,
      failed: 0,
      pending: selectedStudents.length,
      current: null,
    });

    let sent = 0,
      failed = 0;

    const failedRows = [];

    for (const student of selectedStudents) {
      if (stopRef.current) break;

      const current = { name: getName(student), mobile: getMobile(student) };

      setProgress({
        total: selectedStudents.length,
        sent,
        failed,
        pending: selectedStudents.length - sent - failed,
        current,
      });

      try {
        await sendOne(student);

        sent += 1;
      } catch (e) {
        failed += 1;

        const row = {
          student: getName(student),
          mobile: getMobile(student),
          error: e?.message || "Failed",
        };

        failedRows.push(row);

        setFailures([...failedRows]);
      }

      setProgress({
        total: selectedStudents.length,
        sent,
        failed,
        pending: selectedStudents.length - sent - failed,
        current,
      });

      if (!stopRef.current)
        await new Promise((resolve) => setTimeout(resolve, SEND_DELAY_MS));
    }

    const stopped = stopRef.current;

    setSending(false);

    setProgress((p) => ({
      ...p,
      pending: Math.max(0, p.total - sent - failed),
      current: null,
    }));

    if (stopped) {
      SweetAlertShow(
        "info",
        "Sending Stopped",
        `${sent} sent, ${failed} failed. Remaining messages were not sent.`,
      );
    } else if (failed) {
      SweetAlertShow(
        "warning",
        "Completed with failures",
        `${sent} sent and ${failed} failed.`,
      );
    } else {
      SweetAlertShow(
        "success",
        "All Sent",
        `${sent} push notifications sent successfully.`,
      );
    }
  };

  const handleStop = () => {
    stopRef.current = true;
  };

  useEffect(() => {
    if (!sending) return undefined;

    const beforeUnload = (e) => {
      e.preventDefault();

      e.returnValue =
        "Push notifications are still sending. Are you sure you want to leave?";

      return e.returnValue;
    };

    // Protect browser Back while sending. We put one temporary history

    // entry in place so the user can choose Stay or Stop & Leave.

    const guardState = { __pushNotificationGuard: true };

    window.history.pushState(guardState, "", window.location.href);

    const onPopState = () => {
      window.history.pushState(guardState, "", window.location.href);

      pendingNavigationRef.current = { type: "back" };

      setLeaveGuardOpen(true);
    };

    const onDocumentClick = (event) => {
      if (!sending || event.defaultPrevented || event.button !== 0) return;

      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey)
        return;

      const anchor = event.target.closest?.("a[href]");

      if (!anchor) return;

      const href = anchor.getAttribute("href");

      if (
        !href ||
        href.startsWith("#") ||
        href.startsWith("mailto:") ||
        href.startsWith("tel:") ||
        anchor.target === "_blank"
      )
        return;

      try {
        const url = new URL(href, window.location.href);

        if (url.origin !== window.location.origin) return;

        if (url.href === window.location.href) return;

        event.preventDefault();

        event.stopPropagation();

        pendingNavigationRef.current = { type: "link", href: url.href };

        setLeaveGuardOpen(true);
      } catch {
        // Ignore malformed URLs.
      }
    };

    window.addEventListener("beforeunload", beforeUnload);

    window.addEventListener("popstate", onPopState);

    document.addEventListener("click", onDocumentClick, true);

    return () => {
      window.removeEventListener("beforeunload", beforeUnload);

      window.removeEventListener("popstate", onPopState);

      document.removeEventListener("click", onDocumentClick, true);
    };
  }, [sending]);

  const handleReset = () => {
    if (sending) return;

    setForm(INITIAL);

    setStudents([]);

    setBatches([]);

    setBuses([]);

    setCourses([]);

    setFailures([]);

    setProgress({ total: 0, sent: 0, failed: 0, pending: 0, current: null });

    localStorage.removeItem(STORAGE_KEY);
  };

  const progressPercent = progress.total
    ? Math.round(((progress.sent + progress.failed) / progress.total) * 100)
    : 0;

  return (
    <>
      {loading && <LoadingDiv />}

      <div className="layout-wrapper layout-content-navbar">
        <div className="layout-container">
          <Header />

          <div className="layout-page">
            <Nav />

            <div className="content-wrapper">
              <div className="container-fluid flex-grow-1 container-p-y">
                <div className="row">
                  <Master />
                  <div className="col-12">
                    <div className="card border-0 shadow-sm overflow-hidden">
                      <div className="card-body p-0">
                        <div className="p-4 p-lg-5 border-bottom bg-label-primary">
                          <div className="d-flex flex-wrap justify-content-between gap-3 align-items-center">
                            <div>
                              <div className="badge bg-primary mb-2">
                                <i className="ri-notification-3-line me-1" />{" "}
                                Push Notifications
                              </div>

                              <h3 className="mb-1">Send App Notification</h3>

                              <p className="mb-0 text-muted">
                                Select students or staff and send a personalized
                                app notification.
                              </p>
                            </div>

                            <div className="text-end">
                              <div className="display-6 fw-bold">
                                {selectedStudents.length}
                              </div>
                              <small className="text-muted">Recipients</small>
                            </div>
                          </div>
                        </div>

                        <div className="p-4 p-lg-5">
                          <div className="mb-4">
                            <div className="d-flex align-items-center gap-2 mb-3">
                              <span className="badge bg-primary rounded-circle">
                                1
                              </span>
                              <div>
                                <h5 className="mb-0">Choose Audience</h5>
                                <small className="text-muted">
                                  Choose Batch or Bus. Select the required batch
                                  or bus, then choose which students receive the
                                  notification.
                                </small>
                              </div>
                            </div>

                            {form.audienceType !== "staff" && (
                              <div className="row g-3 mb-3">
                                <div className="col-lg-6">
                                  <label className="form-label fw-semibold">
                                    Session
                                  </label>
                                  <select
                                    className="form-select form-select-lg"
                                    value={form.sessionId}
                                    onChange={(e) =>
                                      setForm((p) => ({
                                        ...p,
                                        sessionId: e.target.value,
                                        batchIds: [],
                                        busId: "",
                                        courseIds: [],
                                        studentIds: [],
                                      }))
                                    }
                                  >
                                    <option value="">Select session</option>
                                    {sessions.map((session) => (
                                      <option
                                        key={session.id}
                                        value={session.id}
                                      >
                                        {session.session ||
                                          session.name ||
                                          session.session_name ||
                                          `Session ${session.id}`}
                                        {Number(session?.status) === 1
                                          ? " (active)"
                                          : ""}
                                      </option>
                                    ))}
                                  </select>
                                </div>

                                <div className="col-lg-6 d-flex align-items-end">
                                  <div className="w-100 border rounded-3 px-3 py-2 bg-label-primary">
                                    <div className="d-flex justify-content-between align-items-center">
                                      <div>
                                        <div className="small text-muted">
                                          Selected Session
                                        </div>
                                        <div className="fw-bold text-dark">
                                          {sessionName || "No session selected"}
                                        </div>
                                        {form.audienceType === "batch" &&
                                          !!selectedBatchNames.length && (
                                            <div
                                              className="small text-muted mt-1 text-truncate"
                                              style={{ maxWidth: 360 }}
                                            >
                                              {selectedBatchNames.join(", ")}
                                            </div>
                                          )}
                                        {form.audienceType === "bus" &&
                                          selectedBus && (
                                            <div className="small text-muted mt-1">
                                              {getBusNumber(selectedBus)} ·{" "}
                                              {getBusDriver(selectedBus)}
                                            </div>
                                          )}
                                      </div>
                                      <div className="text-end">
                                        <div className="fw-bold text-primary fs-5">
                                          {selectedStudents.length}
                                        </div>
                                        <small className="text-muted">
                                          students
                                        </small>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            )}

                            <div className="row g-2 mb-3">
                              {[
                                {
                                  value: "batch",
                                  label: "Batch",
                                  icon: "ri-group-line",
                                  description: "Choose one or more batches",
                                },
                                {
                                  value: "bus",
                                  label: "Bus",
                                  icon: "ri-bus-line",
                                  description:
                                    "Choose a bus and its assigned students",
                                },
                                {
                                  value: "staff",
                                  label: "Staff",
                                  icon: "ri-user-settings-line",
                                  description:
                                    "Choose staff members / teachers",
                                },
                              ].map((option) => (
                                <div className="col-md-6" key={option.value}>
                                  <button
                                    type="button"
                                    className={`btn w-100 text-start p-3 border ${form.audienceType === option.value ? "btn-primary" : "btn-outline-primary"}`}
                                    onClick={() =>
                                      handleAudienceChange(option.value)
                                    }
                                  >
                                    <div className="d-flex align-items-center gap-3">
                                      <i className={`${option.icon} fs-3`} />
                                      <div>
                                        <div className="fw-bold">
                                          {option.value === "staff"
                                            ? "Staff / Teachers"
                                            : `${option.label} Students`}
                                        </div>
                                        <small
                                          className={
                                            form.audienceType === option.value
                                              ? "text-white-50"
                                              : "text-muted"
                                          }
                                        >
                                          {option.description}
                                        </small>
                                      </div>
                                      {form.audienceType === option.value && (
                                        <i className="ri-checkbox-circle-fill ms-auto fs-4" />
                                      )}
                                    </div>
                                  </button>
                                </div>
                              ))}
                            </div>

                            {form.audienceType === "batch" && (
                              <div className="border rounded-4 p-3 p-lg-4 bg-body">
                                <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
                                  <div>
                                    <h6 className="mb-1 fw-bold">Batches</h6>
                                    <small className="text-muted">
                                      Choose one or multiple batches
                                    </small>
                                  </div>

                                  <div className="d-flex flex-wrap align-items-center gap-2">
                                    <span className="badge bg-label-primary px-3 py-2">
                                      {form.batchIds.length} selected
                                    </span>

                                    {!!batches.length && (
                                      <button
                                        type="button"
                                        className="btn btn-sm btn-outline-primary"
                                        onClick={() =>
                                          setField(
                                            "batchIds",
                                            batches.map((batch) =>
                                              String(batch.id),
                                            ),
                                          )
                                        }
                                      >
                                        <i className="ri-checkbox-multiple-line me-1" />
                                        Select All Batches
                                      </button>
                                    )}

                                    {!!form.batchIds.length && (
                                      <button
                                        type="button"
                                        className="btn btn-sm btn-outline-secondary"
                                        onClick={() => setField("batchIds", [])}
                                      >
                                        Clear
                                      </button>
                                    )}
                                  </div>
                                </div>

                                {!form.sessionId ? (
                                  <div className="text-center py-5 text-muted">
                                    <i className="ri-calendar-line fs-1 d-block mb-2" />
                                    Select a session to view batches.
                                  </div>
                                ) : loading ? (
                                  <div className="text-center py-5">
                                    <div
                                      className="spinner-border text-primary"
                                      role="status"
                                    />
                                    <div className="text-muted mt-2">
                                      Loading batches...
                                    </div>
                                  </div>
                                ) : !batches.length ? (
                                  <div className="text-center py-5 text-muted">
                                    <i className="ri-group-line fs-1 d-block mb-2" />
                                    No batches found for this session.
                                  </div>
                                ) : (
                                  <div className="row g-2">
                                    {batches.map((batch) => {
                                      const id = String(batch.id);
                                      const checked =
                                        form.batchIds.includes(id);
                                      const name = getBatchDisplayName(batch);

                                      return (
                                        <div className="col-md-6" key={id}>
                                          <label
                                            className={`d-flex align-items-center gap-3 border rounded-3 px-3 py-3 h-100 ${
                                              checked
                                                ? "border-primary bg-label-primary"
                                                : "bg-body"
                                            }`}
                                            style={{
                                              cursor: "pointer",
                                              minHeight: 60,
                                            }}
                                          >
                                            <input
                                              type="checkbox"
                                              className="form-check-input mt-0"
                                              checked={checked}
                                              onChange={() => {
                                                setForm((prev) => ({
                                                  ...prev,
                                                  audienceType: "batch",
                                                  batchIds: checked
                                                    ? prev.batchIds.filter(
                                                        (batchId) =>
                                                          String(batchId) !==
                                                          id,
                                                      )
                                                    : [...prev.batchIds, id],
                                                }));
                                              }}
                                            />
                                            <div className="flex-grow-1 min-w-0">
                                              <div className="fw-bold text-dark">
                                                {name}
                                              </div>
                                              <div className="small text-muted d-flex flex-wrap gap-2 mt-1">
                                                <span>
                                                  <i className="ri-calendar-line me-1" />
                                                  {sessionName ||
                                                    `Session #${batch?.session_id || "—"}`}
                                                </span>
                                                <span>
                                                  <i className="ri-user-line me-1" />
                                                  {Number(
                                                    batch?.active_batch_students_count ||
                                                      0,
                                                  )}{" "}
                                                  active
                                                </span>
                                              </div>
                                            </div>
                                            <div className="text-end ms-2">
                                              <span
                                                className={`badge ${checked ? "bg-primary" : "bg-label-secondary"}`}
                                              >
                                                {checked
                                                  ? "Selected"
                                                  : "Select"}
                                              </span>
                                            </div>
                                          </label>
                                        </div>
                                      );
                                    })}
                                  </div>
                                )}

                                {!!form.batchIds.length && (
                                  <div className="mt-3 p-3 rounded-3 border bg-light">
                                    <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
                                      <span className="badge bg-primary">
                                        Session: {sessionName || "—"}
                                      </span>
                                      <span className="badge bg-dark">
                                        {form.batchIds.length} Batch
                                        {form.batchIds.length === 1 ? "" : "es"}
                                      </span>
                                    </div>
                                    <div className="small text-muted mb-1">
                                      Selected batches
                                    </div>
                                    <div className="d-flex flex-wrap gap-2">
                                      {selectedBatchNames.map((name, index) => (
                                        <span
                                          key={`${name}-${index}`}
                                          className="badge bg-label-primary px-3 py-2"
                                        >
                                          {name}
                                        </span>
                                      ))}
                                    </div>
                                  </div>
                                )}
                              </div>
                            )}

                            {form.audienceType === "bus" && (
                              <div className="border rounded-4 p-3 p-lg-4 bg-body">
                                <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
                                  <div>
                                    <h6 className="mb-1 fw-bold">Buses</h6>
                                    <small className="text-muted">
                                      Select a bus to load students assigned to
                                      it.
                                    </small>
                                  </div>
                                  <span className="badge bg-label-primary px-3 py-2">
                                    {selectedBus
                                      ? "1 selected"
                                      : "No bus selected"}
                                  </span>
                                </div>

                                {!form.sessionId ? (
                                  <div className="text-center py-5 text-muted">
                                    <i className="ri-calendar-line fs-1 d-block mb-2" />
                                    Select a session to view buses.
                                  </div>
                                ) : loading ? (
                                  <div className="text-center py-5">
                                    <div
                                      className="spinner-border text-primary"
                                      role="status"
                                    />
                                    <div className="text-muted mt-2">
                                      Loading buses...
                                    </div>
                                  </div>
                                ) : !buses.length ? (
                                  <div className="text-center py-5 text-muted">
                                    <i className="ri-bus-line fs-1 d-block mb-2" />
                                    No buses found for this session.
                                  </div>
                                ) : (
                                  <div className="row g-2">
                                    {buses.map((bus) => {
                                      const id = String(bus.id);
                                      const checked = String(form.busId) === id;

                                      return (
                                        <div className="col-md-6" key={id}>
                                          <label
                                            className={`d-flex align-items-center gap-3 border rounded-3 px-3 py-3 h-100 ${checked ? "border-primary bg-label-primary" : "bg-body"}`}
                                            style={{
                                              cursor: "pointer",
                                              minHeight: 76,
                                            }}
                                          >
                                            <input
                                              type="radio"
                                              name="push-notification-bus"
                                              className="form-check-input mt-0"
                                              checked={checked}
                                              onChange={() =>
                                                setForm((prev) => ({
                                                  ...prev,
                                                  busId: id,
                                                  studentIds: [],
                                                }))
                                              }
                                            />
                                            <span className="avatar avatar-sm">
                                              <span className="avatar-initial rounded-circle bg-label-primary">
                                                <i className="ri-bus-line fs-5" />
                                              </span>
                                            </span>
                                            <span className="flex-grow-1 min-w-0">
                                              <span className="d-block fw-bold">
                                                {getBusNumber(bus)}
                                              </span>
                                              <span className="d-block small text-muted">
                                                <i className="ri-user-line me-1" />
                                                {getBusDriver(bus)}
                                              </span>
                                            </span>
                                            {checked && (
                                              <i className="ri-checkbox-circle-fill text-primary fs-4" />
                                            )}
                                          </label>
                                        </div>
                                      );
                                    })}
                                  </div>
                                )}
                              </div>
                            )}
                            {form.audienceType === "staff" && (
                              <div className="border rounded-4 p-3 p-lg-4 bg-body">
                                <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
                                  <div>
                                    <h6 className="mb-1 fw-bold">Staff List</h6>
                                    <small className="text-muted">
                                      Select staff members who should receive
                                      the notification.
                                    </small>
                                  </div>
                                  <div className="d-flex gap-2 align-items-center">
                                    <span className="badge bg-label-primary px-3 py-2">
                                      {form.studentIds.length} selected
                                    </span>
                                    {!!staffMembers.length && (
                                      <button
                                        type="button"
                                        className="btn btn-sm btn-outline-secondary"
                                        onClick={() =>
                                          setField("studentIds", [])
                                        }
                                      >
                                        Clear
                                      </button>
                                    )}
                                  </div>
                                </div>
                                {loadingStaff ? (
                                  <div className="text-center py-5">
                                    <div
                                      className="spinner-border text-primary"
                                      role="status"
                                    />
                                    <div className="text-muted mt-2">
                                      Loading staff...
                                    </div>
                                  </div>
                                ) : !staffMembers.length ? (
                                  <div className="text-center py-5 text-muted">
                                    <i className="ri-user-search-line fs-1 d-block mb-2" />
                                    No staff found.
                                  </div>
                                ) : (
                                  <>
                                    <div className="d-flex flex-wrap gap-2 mb-3">
                                      <button
                                        type="button"
                                        className="btn btn-sm btn-outline-primary"
                                        onClick={() =>
                                          setField(
                                            "studentIds",
                                            staffMembers.map((staff) =>
                                              String(staff.__id),
                                            ),
                                          )
                                        }
                                      >
                                        <i className="ri-checkbox-multiple-line me-1" />{" "}
                                        Select All
                                      </button>
                                      <button
                                        type="button"
                                        className="btn btn-sm btn-outline-secondary"
                                        onClick={() =>
                                          setField("studentIds", [])
                                        }
                                      >
                                        Unselect All
                                      </button>
                                    </div>
                                    <div
                                      className="border rounded-3"
                                      style={{
                                        maxHeight: 360,
                                        overflowY: "auto",
                                      }}
                                    >
                                      {staffMembers.map((staff) => {
                                        const id = String(
                                          staff.__id || getRecipientKey(staff),
                                        );
                                        const checked = form.studentIds
                                          .map(String)
                                          .includes(id);
                                        return (
                                          <label
                                            key={id}
                                            className={`d-flex align-items-center gap-3 px-3 py-3 border-bottom ${checked ? "bg-label-primary" : ""}`}
                                            style={{ cursor: "pointer" }}
                                          >
                                            <input
                                              type="checkbox"
                                              className="form-check-input"
                                              checked={checked}
                                              onChange={() =>
                                                setField(
                                                  "studentIds",
                                                  checked
                                                    ? form.studentIds.filter(
                                                        (value) =>
                                                          String(value) !== id,
                                                      )
                                                    : [...form.studentIds, id],
                                                )
                                              }
                                            />
                                            <span className="avatar avatar-sm">
                                              <span className="avatar-initial rounded-circle bg-label-primary">
                                                {getName(staff)
                                                  .charAt(0)
                                                  .toUpperCase()}
                                              </span>
                                            </span>
                                            <span className="flex-grow-1 min-w-0">
                                              <span className="d-block fw-semibold">
                                                {getName(staff)}
                                              </span>
                                              <span className="small text-muted">
                                                {staff?.role_info?.name ||
                                                  staff?.designation ||
                                                  staff?.registration_no ||
                                                  "Staff"}
                                                {" · "}
                                                {getMobile(staff) ||
                                                  "No mobile"}
                                              </span>
                                            </span>
                                            {checked && (
                                              <i className="ri-checkbox-circle-fill text-primary fs-4" />
                                            )}
                                          </label>
                                        );
                                      })}
                                    </div>
                                  </>
                                )}
                              </div>
                            )}
                          </div>

                          <div className="mb-4">
                            <div className="d-flex align-items-center gap-2 mb-3">
                              <span className="badge bg-primary rounded-circle">
                                2
                              </span>
                              <h5 className="mb-0">
                                {form.audienceType === "staff"
                                  ? "Selected Staff"
                                  : form.audienceType === "bus"
                                    ? "Selected Bus Students"
                                    : "Selected Batch Students"}
                              </h5>
                            </div>

                            {form.audienceType === "staff" ? (
                              <div className="alert alert-primary mb-0 d-flex flex-wrap justify-content-between align-items-center gap-2">
                                <div>
                                  <i className="ri-user-settings-line me-2" />
                                  <strong>
                                    {selectedStudents.length}
                                  </strong>{" "}
                                  staff members selected.
                                </div>
                              </div>
                            ) : form.audienceType === "batch" &&
                              !form.batchIds.length ? (
                              <div className="alert alert-light border mb-0">
                                <i className="ri-information-line me-2" />
                                Select at least one batch. Students from
                                selected batches will be included automatically.
                              </div>
                            ) : form.audienceType === "bus" && !form.busId ? (
                              <div className="alert alert-light border mb-0">
                                <i className="ri-information-line me-2" />
                                Select a bus to load its assigned students.
                              </div>
                            ) : loadingStudents ? (
                              <div className="text-center py-4">
                                <div className="spinner-border spinner-border-sm text-primary" />
                                <span className="ms-2 text-muted">
                                  Loading students...
                                </span>
                              </div>
                            ) : (
                              <div className="alert alert-primary mb-0 d-flex flex-wrap justify-content-between align-items-center gap-2">
                                <div>
                                  <i
                                    className={
                                      form.audienceType === "bus"
                                        ? "ri-bus-line me-2"
                                        : "ri-group-line me-2"
                                    }
                                  />
                                  <strong>{selectedStudents.length}</strong>{" "}
                                  students ready{" "}
                                  {form.audienceType === "bus"
                                    ? "from"
                                    : "from"}{" "}
                                  {form.audienceType === "bus" ? (
                                    <strong>
                                      {selectedBus
                                        ? getBusNumber(selectedBus)
                                        : "selected bus"}
                                    </strong>
                                  ) : (
                                    <>
                                      <strong>{form.batchIds.length}</strong>{" "}
                                      batch
                                      {form.batchIds.length === 1 ? "" : "es"}
                                    </>
                                  )}
                                  .
                                </div>
                                <button
                                  type="button"
                                  className="btn btn-sm btn-outline-primary"
                                  disabled={!selectedStudents.length}
                                  onClick={() => setPreviewOpen(true)}
                                >
                                  <i className="ri-eye-line me-1" />
                                  Select / Edit Students
                                </button>
                              </div>
                            )}
                          </div>

                          <div className="mb-4">
                            <div className="d-flex align-items-center gap-2 mb-3">
                              <span className="badge bg-primary rounded-circle">
                                3
                              </span>
                              <h5 className="mb-0">Compose Message</h5>
                            </div>

                            <div className="row g-3">
                              <div className="col-12">
                                <label className="form-label fw-semibold">
                                  Title
                                </label>
                                <input
                                  className="form-control form-control-lg"
                                  value={form.title}
                                  onChange={(e) =>
                                    setField("title", e.target.value)
                                  }
                                  placeholder="e.g. Fee Reminder"
                                  maxLength={150}
                                />
                              </div>

                              <div className="col-12">
                                <label className="form-label fw-semibold">
                                  Message
                                </label>

                                <textarea
                                  className="form-control"
                                  rows={7}
                                  value={form.message}
                                  onChange={(e) =>
                                    setField("message", e.target.value)
                                  }
                                  placeholder="Write your notification message... Use dynamic fields below."
                                />

                                <div className="d-flex flex-wrap gap-2 mt-2">
                                  {dynamicFields.slice(0, 25).map((field) => (
                                    <button
                                      key={field}
                                      type="button"
                                      className="btn btn-sm btn-outline-primary"
                                      onClick={() => insertVariable(field)}
                                    >
                                      {formatFieldLabel(field)}
                                    </button>
                                  ))}
                                </div>

                                <small className="text-muted d-block mt-2">
                                  Click a field to insert it. Example:{" "}
                                  <code>{"{{name}}"}</code>,{" "}
                                  <code>{"{{batch_total_due}}"}</code>,{" "}
                                  <code>{"{{batch_name}}"}</code>.
                                </small>
                              </div>

                              <div className="col-lg-8">
                                <label className="form-label fw-semibold">
                                  Image URL{" "}
                                  <span className="text-muted fw-normal">
                                    (optional)
                                  </span>
                                </label>
                                <input
                                  type="url"
                                  className="form-control"
                                  value={form.file}
                                  onChange={(e) =>
                                    setField("file", e.target.value)
                                  }
                                  placeholder="https://example.com/image.jpg"
                                />
                              </div>
                            </div>
                          </div>

                          {(form.title || form.message) && (
                            <div className="card border mb-4">
                              <div className="card-header d-flex justify-content-between align-items-center">
                                <span className="fw-semibold">
                                  <i className="ri-eye-line me-1" /> Live
                                  Preview
                                </span>
                                <span className="badge bg-label-primary">
                                  {getName(previewStudent)}
                                </span>
                              </div>

                              <div className="card-body">
                                <div className="fw-bold fs-5">
                                  {previewTitle || "Notification title"}
                                </div>
                                <div
                                  className="mt-2 text-muted"
                                  style={{ whiteSpace: "pre-wrap" }}
                                >
                                  {previewMessage ||
                                    "Your message preview will appear here."}
                                </div>
                              </div>
                            </div>
                          )}

                          <div className="d-flex flex-wrap gap-2">
                            <button
                              type="button"
                              className="btn btn-outline-primary btn-lg"
                              disabled={
                                !selectedStudents.length ||
                                (!form.title && !form.message)
                              }
                              onClick={() => setMessagePreviewOpen(true)}
                            >
                              <i className="ri-eye-line me-1" /> Preview All
                              Messages
                            </button>

                            <button
                              type="button"
                              className="btn btn-primary btn-lg"
                              disabled={sending || !selectedStudents.length}
                              onClick={handleSend}
                            >
                              <i className="ri-send-plane-2-line me-1" /> Send
                              to {selectedStudents.length || "students"}
                            </button>

                            <button
                              type="button"
                              className="btn btn-outline-secondary btn-lg"
                              disabled={sending}
                              onClick={handleReset}
                            >
                              Reset
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {sending && (
        <div
          className="position-fixed bottom-0 end-0 p-3"
          style={{ zIndex: 2000, width: "min(440px, calc(100vw - 24px))" }}
        >
          <div className="card shadow-lg border-0">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <div className="fw-semibold">
                  <i className="ri-loader-4-line text-primary me-2" />
                  Sending notifications...
                </div>
                <span className="fw-bold">{progressPercent}%</span>
              </div>

              <div className="progress mb-3" style={{ height: 8 }}>
                <div
                  className="progress-bar progress-bar-striped progress-bar-animated"
                  style={{ width: `${progressPercent}%` }}
                />
              </div>

              <div className="row g-2 text-center mb-3">
                <div className="col-4">
                  <div className="fw-bold text-success">{progress.sent}</div>
                  <small>Sent</small>
                </div>
                <div className="col-4">
                  <div className="fw-bold text-danger">{progress.failed}</div>
                  <small>Failed</small>
                </div>
                <div className="col-4">
                  <div className="fw-bold">{progress.pending}</div>
                  <small>Pending</small>
                </div>
              </div>

              {progress.current && (
                <div className="small bg-label-primary rounded p-2 mb-3">
                  <strong>Current:</strong> {progress.current.name}
                  <br />
                  {progress.current.mobile || "No mobile"}
                </div>
              )}

              <button
                type="button"
                className="btn btn-outline-danger w-100"
                onClick={handleStop}
              >
                <i className="ri-stop-circle-line me-1" /> Stop after current
                request
              </button>
            </div>
          </div>
        </div>
      )}

      {failures.length > 0 && !sending && (
        <div
          className="position-fixed bottom-0 start-0 p-3"
          style={{ zIndex: 1500 }}
        >
          <button
            type="button"
            className="btn btn-danger shadow"
            onClick={() => setPreviewOpen(true)}
          >
            <i className="ri-error-warning-line me-1" /> {failures.length}{" "}
            failed
          </button>
        </div>
      )}

      {previewOpen && (
        <div
          className="modal d-block"
          tabIndex="-1"
          style={{ background: "rgba(20, 18, 35, .62)", zIndex: 2100 }}
        >
          <div className="modal-dialog modal-xl modal-dialog-scrollable modal-dialog-centered">
            <div className="modal-content border-0 shadow-lg rounded-4 overflow-hidden">
              <div className="modal-header border-bottom px-4 py-3 bg-body">
                <div>
                  <h5 className="modal-title mb-1">
                    <i className="ri-group-line text-primary me-2" />
                    {form.audienceType === "staff"
                      ? "Select Staff"
                      : "Select Students"}
                  </h5>
                  <div className="small text-muted">
                    {form.audienceType === "staff"
                      ? "Staff list"
                      : `${sessionName || "Selected session"} • `}
                    {form.audienceType === "bus"
                      ? selectedBus
                        ? `${getBusNumber(selectedBus)} · ${getBusDriver(selectedBus)}`
                        : "Selected bus"
                      : form.audienceType === "staff"
                        ? "Selected staff members"
                        : selectedBatchNames.length
                          ? selectedBatchNames.join(", ")
                          : "Selected batches"}
                  </div>
                </div>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setPreviewOpen(false)}
                />
              </div>

              <div className="modal-body p-4">
                {failures.length ? (
                  <div>
                    {failures.map((f, i) => (
                      <div
                        key={i}
                        className="d-flex justify-content-between gap-3 border-bottom py-2"
                      >
                        <div>
                          <strong>{f.student}</strong>
                          <div className="small text-muted">{f.mobile}</div>
                        </div>
                        <span className="text-danger small">{f.error}</span>
                      </div>
                    ))}
                  </div>
                ) : form.audienceType === "staff" ? (
                  <div>
                    <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
                      <div className="fw-semibold">
                        {staffMembers.length} staff members
                      </div>
                      <div className="d-flex gap-2">
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-primary"
                          onClick={() =>
                            setField(
                              "studentIds",
                              staffMembers.map((staff) => String(staff.__id)),
                            )
                          }
                        >
                          Select All
                        </button>
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-secondary"
                          onClick={() => setField("studentIds", [])}
                        >
                          Clear
                        </button>
                      </div>
                    </div>
                    {staffMembers.map((staff) => {
                      const id = String(staff.__id || getRecipientKey(staff));
                      const checked = form.studentIds.map(String).includes(id);
                      return (
                        <label
                          key={id}
                          className={`d-flex align-items-center gap-3 px-3 py-3 border-bottom ${checked ? "bg-label-primary" : ""}`}
                          style={{ cursor: "pointer" }}
                        >
                          <input
                            type="checkbox"
                            className="form-check-input"
                            checked={checked}
                            onChange={() =>
                              setField(
                                "studentIds",
                                checked
                                  ? form.studentIds.filter(
                                      (value) => String(value) !== id,
                                    )
                                  : [...form.studentIds, id],
                              )
                            }
                          />
                          <div className="flex-grow-1">
                            <div className="fw-semibold">{getName(staff)}</div>
                            <small className="text-muted">
                              {staff?.role_info?.name ||
                                staff?.designation ||
                                "Staff"}{" "}
                              · {getMobile(staff) || "No mobile"}
                            </small>
                          </div>
                        </label>
                      );
                    })}
                  </div>
                ) : (
                  <StudentPicker
                    students={filteredAudienceStudents}
                    values={form.studentIds}
                    onChange={(ids) => setField("studentIds", ids)}
                    feeFilter={feeFilter}
                    setFeeFilter={setFeeFilter}
                    rteFilter={rteFilter}
                    setRteFilter={setRteFilter}
                    studentTypeFilter={studentTypeFilter}
                    setStudentTypeFilter={setStudentTypeFilter}
                  />
                )}
              </div>

              <div className="modal-footer border-top px-4 py-3 d-flex justify-content-between">
                <div className="small text-muted">
                  <strong>{form.studentIds.length}</strong> of{" "}
                  <strong>{filteredAudienceStudents.length}</strong> students
                  selected
                </div>
                <div className="d-flex gap-2">
                  {!failures.length && (
                    <button
                      type="button"
                      className="btn btn-outline-secondary"
                      onClick={() => setField("studentIds", [])}
                    >
                      Unselect All
                    </button>
                  )}
                  <button
                    type="button"
                    className="btn btn-primary px-4"
                    onClick={() => setPreviewOpen(false)}
                  >
                    <i className="ri-check-line me-1" /> Done
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {messagePreviewOpen && (
        <div
          className="modal d-block"
          tabIndex="-1"
          style={{ background: "rgba(0,0,0,.55)", zIndex: 2150 }}
        >
          <div className="modal-dialog modal-xl modal-dialog-scrollable modal-dialog-centered">
            <div className="modal-content border-0 shadow-lg">
              <div className="modal-header">
                <div>
                  <h5 className="modal-title mb-1">Message Preview</h5>
                  <small className="text-muted">
                    Resolved separately for all {selectedStudents.length}{" "}
                    selected students
                  </small>
                </div>

                <button
                  className="btn-close"
                  onClick={() => setMessagePreviewOpen(false)}
                />
              </div>

              <div className="modal-body">
                {selectedStudents.map((student, index) => (
                  <div
                    key={`${getId(student)}-${index}`}
                    className="card border mb-3"
                  >
                    <div className="card-header d-flex justify-content-between align-items-center">
                      <div>
                        <strong>{getName(student)}</strong>
                        <div className="small text-muted">
                          {getStudentBatchDisplayName(student) ||
                            getCourseName(student) ||
                            "—"}{" "}
                          • {sessionName || "No session"} •{" "}
                          {getMobile(student) || "No mobile"}
                        </div>
                      </div>

                      <span className="badge bg-label-primary">
                        #{index + 1}
                      </span>
                    </div>

                    <div className="card-body">
                      <div className="fw-bold fs-5">
                        {resolveMessage(form.title, student) ||
                          "Notification title"}
                      </div>

                      <div className="mt-2" style={{ whiteSpace: "pre-wrap" }}>
                        {resolveMessage(form.message, student) || "No message"}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="modal-footer">
                <button
                  className="btn btn-secondary"
                  onClick={() => setMessagePreviewOpen(false)}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {leaveGuardOpen && (
        <div
          className="modal d-block"
          style={{ background: "rgba(0,0,0,.55)", zIndex: 2200 }}
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content border-0 shadow-lg">
              <div className="modal-header">
                <div>
                  <span className="badge bg-warning text-dark mb-2">
                    Sending in progress
                  </span>
                  <h5 className="modal-title">Leave this page?</h5>
                </div>
              </div>
              <div className="modal-body">
                <p className="mb-2">Push notifications are still being sent.</p>
                <p className="text-muted mb-0">
                  <strong>{progress.sent}</strong> sent,{" "}
                  <strong>{progress.failed}</strong> failed,{" "}
                  <strong>{progress.pending}</strong> pending.
                </p>
                <div className="alert alert-warning mt-3 mb-0">
                  <i className="ri-error-warning-line me-1" /> If you leave now,
                  the browser-based sending process will stop.
                </div>
              </div>
              <div className="modal-footer">
                <button
                  className="btn btn-outline-secondary"
                  onClick={() => {
                    pendingNavigationRef.current = null;
                    setLeaveGuardOpen(false);
                  }}
                >
                  Stay & Continue
                </button>
                <button
                  className="btn btn-danger"
                  onClick={() => {
                    const target = pendingNavigationRef.current;
                    handleStop();
                    setLeaveGuardOpen(false);
                    pendingNavigationRef.current = null;
                    setTimeout(() => {
                      if (target?.type === "link" && target.href)
                        window.location.href = target.href;
                      else if (target?.type === "back") window.history.go(-2);
                    }, 50);
                  }}
                >
                  Stop & Leave
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
